import { LitElement, html, css } from 'lit';

/**
 * Composant <ui-checkbox>
 * Case à cocher élégante avec animation SVG et accessibilité clavier
 */
export class UICheckbox extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    checked: { type: Boolean, reflect: true },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    value: { type: String },
    label: { type: String, reflect: true }
  };

  static styles = css`
    :host {
      display: inline-flex;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
      cursor: pointer;
      user-select: none;
    }

    :host([label]) {
      display: block;
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .checkbox-container {
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    :host([disabled]) {
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* Le carré de la checkbox */
    .checkbox-box {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: var(--ui-surface);
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    :host(:hover:not([disabled])) .checkbox-box {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .checkbox-box:focus-visible {
      border-color: var(--ui-border-focus);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    /* Checkbox quand cochée */
    :host([checked]) .checkbox-box {
      background-color: var(--ui-primary);
      border-color: var(--ui-primary);
    }

    :host([checked]:hover:not([disabled])) .checkbox-box {
      background-color: var(--ui-primary-hover);
      border-color: var(--ui-primary-hover);
    }

    /* L'icône de coche (checkmark) */
    .checkbox-check {
      width: 12px;
      height: 12px;
      color: var(--ui-text-on-primary);
      fill: none;
      stroke: currentColor;
      stroke-width: 3.5;
      stroke-linecap: round;
      stroke-linejoin: round;
      opacity: 0;
      transform: scale(0.6);
      transition: transform var(--ui-transition-fast) cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity var(--ui-transition-fast);
    }

    :host([checked]) .checkbox-check {
      opacity: 1;
      transform: scale(1);
    }

    .checkbox-label {
      line-height: 1;
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.checked = false;
    this.disabled = false;
    this.required = false;
    this.value = 'on';
    this.label = '';

    this.addEventListener('click', this._handleClick);
    this.addEventListener('keydown', this._handleKeyDown);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('checked') || changedProperties.has('required') || changedProperties.has('value')) {
      this.internals.setFormValue(this.checked ? this.value || 'on' : null);
      this._updateValidity();
    }
  }

  _updateValidity() {
    if (this.required && !this.checked) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez cocher cette case.', this.shadowRoot.querySelector('.checkbox-box'));
    } else {
      this.internals.setValidity({});
    }
  }

  _handleClick(e) {
    if (this.disabled) return;
    e.preventDefault();
    this._toggle();
  }

  _handleKeyDown(e) {
    if (this.disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._toggle();
    }
  }

  _toggle() {
    const nextState = !this.checked;
    this.checked = nextState;

    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('ui-change', {
      detail: { checked: nextState, value: nextState ? this.value : '' },
      bubbles: true,
      composed: true
    }));
  }

  render() {
    return html`
      <!-- Soumission automatique gérée via ElementInternals -->
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="checkbox-container">
        <div 
          class="checkbox-box" part="input" 
          tabindex="${this.disabled ? '-1' : '0'}"
          role="checkbox"
          aria-checked="${this.checked}"
          aria-disabled="${this.disabled}"
        >
          <svg class="checkbox-check" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
        <span class="checkbox-label">
          <slot></slot>
        </span>
      </div>
    `;
  }
}

if (!customElements.get('ui-checkbox')) {
  customElements.define('ui-checkbox', UICheckbox);
}
